import { Box, Flex, Spinner } from 'front-end/src/components'
import { Alert, Textarea } from '@chakra-ui/react'

import { brandColors } from 'shared/basicPalette'
import { CustomTranslationModel } from 'front-end/src/graphql/private/PrivateSchemaTypes'

import React, { useEffect, useRef, useState } from 'react'

import {
  CustomTranslationsTabDocument,
  useDeleteTranslationMessageMutation,
  useSetCustomTranslationMutation
} from './CustomTranslationsTab.codegen'
import { t, Trans } from '@lingui/macro'
import {
  linguiCompileMessage,
  linguiDecompileMessage
} from 'shared/linguiCompileMessage'

import IconCheck from 'front-end/src/components/common/icon/IconCheck'
import { css } from '@emotion/react'
import { SpinnerSizeEnum } from 'front-end/src/components/common/spinner/Spinner'
import { isEqual } from 'lodash'
import Tooltip from 'front-end/src/components/common/tooltip/Tooltip'
import IconCopy from 'front-end/src/components/common/icon/IconCopy'

export type EditableTranslationType = CustomTranslationModel & {
  customTranslation?: string
}

export const EditableTranslationTableRow: React.FC<EditableTranslationType> = (
  props
) => {
  const [validationErrors, setValidationErrors] = useState<string[]>([])

  const { term, locale, app_section } = props

  let translation: string
  const borderColor = validationErrors.length === 0 ? '' : brandColors.red[100]
  const [deleteTranslation, { loading: deleteLoading }] =
    useDeleteTranslationMessageMutation()
  const [setCustom, { loading }] = useSetCustomTranslationMutation()
  const textAreaEl = useRef<HTMLTextAreaElement>(null)
  const [justSaved, setJustSaved] = useState(false)
  const [customTranslation, setCustomTranslation] = useState(
    props.customTranslation
  )

  useEffect(() => {
    if (justSaved) {
      setTimeout(() => {
        setJustSaved(false)
      }, 2000)
    }
  }, [justSaved])

  const refetchQueries = [
    {
      query: CustomTranslationsTabDocument,
      variables: {
        locale,
        appSection: app_section
      }
    }
  ]
  translation = linguiDecompileMessage(props.term)

  const runValidation = (msg: string) => {
    const errors: string[] = []

    if (msg.trim() === '') {
      errors.push(t`String cannot be just whitespace`)
    }
    try {
      linguiCompileMessage(msg)
    } catch (err) {
      console.error(err)

      if (msg.includes('plural')) {
        errors.push(
          t`The string contains invalid definition of plural terms. Please ensure format matches the original format. Only words that can be edited are within the inner curly brackets.`
        )
      } else {
        errors.push(err.message)
      }
    }
    const msgVars = msg.match(/{(.*)}/g)
    const msgTags = msg.match(/<(.*)\/>/g)

    if (
      msg.match(/{/g)?.length !== translation.match(/{/g)?.length ||
      msg.match(/}/g)?.length !== translation.match(/}/g)?.length ||
      (translation.includes('plural') === false &&
        isEqual(msgVars, translation.match(/{(.*)}/g)) === false)
    ) {
      errors.push(
        t`The strings contains invalid variable definition.Please ensure the number of variables matches the number used in the original string. A variable is enclosed in curly brackets.`
      )
    } else if (
      msg.match(/</g)?.length !== translation.match(/</g)?.length ||
      msg.match(/>/g)?.length !== translation.match(/>/g)?.length ||
      isEqual(msgTags, translation.match(/<(.*)\/>/g)) === false
    ) {
      errors.push(
        t`The strings contains some invalid tags. Please ensure the tags match the original string. A tag is enclosed in <>.`
      )
    }
    setValidationErrors(errors)

    return errors
  }
  const storeToBackend = async () => {
    if (customTranslation) {
      const errors = runValidation(customTranslation)
      if (errors.length === 0) {
        await setCustom({
          variables: {
            customTranslation: {
              term,
              translation: customTranslation,
              locale,
              app_section
            }
          },
          refetchQueries
        })
        setJustSaved(true)
      }
    }
  }
  const isInvalid = validationErrors.length > 0

  const isDifferent = customTranslation !== props.customTranslation

  return (
    <Flex justify="center" direction="column" flex="1" height="100%">
      <Flex
        flex="1"
        align="flex-start"
        alignItems="center"
        mr="8px"
        css={css`
          transition: all 0.4s ease;
          .IconDoc {
            opacity: 0;
          }
          :hover {
            .IconDoc {
              opacity: 1;
            }
          }
        `}
        data-test="TranslationsTableRow"
      >
        <Flex align="center" width="50%" data-test="OriginalTranslation">
          {translation}
          <Box ml="auto">
            {justSaved ? (
              <Box mr={'4px'} color={brandColors.success}>
                <Trans>Saved</Trans>
              </Box>
            ) : loading || deleteLoading ? (
              <Spinner size={SpinnerSizeEnum.small} />
            ) : (
              <Tooltip
                text={<Trans>Clone from original translation</Trans>}
                dataTest="CloneButton"
              >
                <IconCopy
                  m="8px"
                  cursor="pointer"
                  onClick={() => {
                    setCustomTranslation(translation)
                    textAreaEl.current?.focus()
                  }}
                />
              </Tooltip>
            )}
          </Box>
        </Flex>

        <Flex flexDir="column" width="50%">
          <Flex>
            <Textarea
              whiteSpace="pre-wrap"
              borderColor={borderColor}
              value={customTranslation}
              maxW="50vw"
              minH="40px"
              maxLength={255}
              maxH="100px"
              ref={textAreaEl}
              onChange={async (ev) => {
                const value = ev.target.value
                setCustomTranslation(value)
                if (isInvalid) {
                  runValidation(value)
                }
              }}
              onBlur={() => {
                if (!props.customTranslation && !customTranslation) {
                  // both are falsy
                  return
                }
                if (isDifferent) {
                  if (!customTranslation) {
                    deleteTranslation({
                      variables: {
                        term,
                        locale
                      },
                      refetchQueries
                    }).then(() => {
                      setJustSaved(true)
                    })
                  } else {
                    storeToBackend()
                  }
                }
              }}
              data-test="TranslationInput"
            />

            {Boolean(customTranslation && isDifferent) && (
              <Flex flexDir="column" m={2}>
                <IconCheck
                  size="medium"
                  cursor={isInvalid ? 'initial' : 'pointer'}
                  opacity={isInvalid ? 0.3 : 1}
                  dataTest="SaveCustomTranslation"
                />
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
      {isInvalid && (
        <Alert
          data-test="ErrorMessage"
          status="error"
          borderRadius="4px"
          p="8px"
          mt="4px"
          overflow="auto"
          align="start"
        >
          <ul>
            {validationErrors.map((e) => {
              return <li key={e}>{e}</li>
            })}
          </ul>
        </Alert>
      )}
    </Flex>
  )
}
